import React, { useState } from 'react';
import { Player, Payment } from '../types';

interface TournamentFeesProps {
  players: Player[];
  payments: Payment[];
  config: { tournamentFee: number; transportTotal: number; teamName: string };
  onUpdatePayments: (payments: Payment[]) => void;
}

export const TournamentFees: React.FC<TournamentFeesProps> = ({ players, payments, config, onUpdatePayments }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingPaidColumn, setEditingPaidColumn] = useState<string | null>(null);
  const [editingPendingColumn, setEditingPendingColumn] = useState<string | null>(null);
  const [editPaidAmount, setEditPaidAmount] = useState('');
  const [editPendingAmount, setEditPendingAmount] = useState('');

  const tournamentPerHead = config.tournamentFee;
  const activePlayerCount = players.filter(p => p.isActive).length;

  const pendingCount = players.filter(p => {
    if (!p.isActive) return false;
    const playerPaid = payments
      .filter(pay => pay.playerId === p.id && pay.categoryId === 'tournament' && pay.isPaid)
      .reduce((sum, pay) => sum + pay.amount, 0);
    return playerPaid < tournamentPerHead;
  }).length;

  // Get player payment status
  const getPlayerPaymentInfo = (playerId: string) => {
    const playerPayments = payments.filter(p => p.playerId === playerId && p.categoryId === 'tournament' && p.isPaid);
    const totalPaid = playerPayments.reduce((sum, p) => sum + p.amount, 0);
    const pending = tournamentPerHead - totalPaid;

    return {
      paid: totalPaid,
      pending: pending > 0 ? pending : 0,
      status: pending <= 0 ? 'Full Paid' : totalPaid > 0 ? 'Partially Paid' : '--'
    };
  };

  // Add or update payment
  const updatePlayerPayment = (playerId: string, amount: number) => {
    if (amount < 0) return;

    // Remove existing tournament payments for this player
    const filteredPayments = payments.filter(p =>
      !(p.playerId === playerId && p.categoryId === 'tournament')
    );

    // Add new payment only if amount > 0
    if (amount > 0) {
      const newPayment: Payment = {
        id: Date.now().toString(),
        playerId,
        categoryId: 'tournament',
        amount,
        isPaid: true,
        paidAt: new Date(),
        isAdvance: false,
        isPartialPayment: amount < config.tournamentFee
      };

      onUpdatePayments([...filteredPayments, newPayment]);
    } else {
      // If amount is 0, just remove existing payments
      onUpdatePayments(filteredPayments);
    }
  };

  // Start editing paid amount
  const startEditPaid = (playerId: string) => {
    const playerInfo = getPlayerPaymentInfo(playerId);
    setEditingPaidColumn(playerId);
    setEditPaidAmount(playerInfo.paid.toString());
  };

  // Start editing pending amount
  const startEditPending = (playerId: string) => {
    const playerInfo = getPlayerPaymentInfo(playerId);
    setEditingPendingColumn(playerId);
    setEditPendingAmount(playerInfo.pending.toString());
  };

  // Save paid amount edit
  const savePaidEdit = () => {
    if (editingPaidColumn && editPaidAmount !== '') {
      const amount = parseFloat(editPaidAmount) || 0;
      updatePlayerPayment(editingPaidColumn, amount);
      setEditingPaidColumn(null);
      setEditPaidAmount('');
    }
  };

  // Save pending amount edit
  const savePendingEdit = () => {
    if (editingPendingColumn && editPendingAmount !== '') {
      const currentPaid = getPlayerPaymentInfo(editingPendingColumn).paid;
      const additionalAmount = parseFloat(editPendingAmount) || 0;
      const newTotalPaid = currentPaid + additionalAmount;
      updatePlayerPayment(editingPendingColumn, newTotalPaid);
      setEditingPendingColumn(null);
      setEditPendingAmount('');
    }
  };

  // Cancel paid edit
  const cancelPaidEdit = () => {
    setEditingPaidColumn(null);
    setEditPaidAmount('');
  };

  // Cancel pending edit
  const cancelPendingEdit = () => {
    setEditingPendingColumn(null);
    setEditPendingAmount('');
  };

  // Download tournament fees summary
  const downloadTournamentSummary = () => {
    const activePlayerCount = players.filter(p => p.isActive).length;
    const totalCollected = payments
      .filter(p => p.categoryId === 'tournament' && p.isPaid)
      .reduce((sum, p) => sum + p.amount, 0);
    const totalRequired = activePlayerCount * tournamentPerHead;
    const outstanding = totalRequired - totalCollected;

    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size for table layout
    const width = 700;
    const headerHeight = 100;
    const rowHeight = 50;
    const tableHeight = (activePlayerCount + 1) * rowHeight; // +1 for header row
    const height = headerHeight + tableHeight + 60;

    canvas.width = width;
    canvas.height = height;

    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    // Header section
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, 0, width, headerHeight);

    // Title
    ctx.fillStyle = 'white';
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('🏏 Tournament Fees Summary', width/2, 35);

    // Summary stats
    ctx.font = '14px Arial';
    ctx.fillText(`Required: ₹${totalRequired.toLocaleString()} | Collected: ₹${totalCollected.toLocaleString()} | Outstanding: ₹${outstanding.toLocaleString()}`, width/2, 65);

    // Table header
    let currentY = headerHeight;
    ctx.fillStyle = '#374151';
    ctx.fillRect(0, currentY, width, rowHeight);

    // Column headers
    ctx.fillStyle = 'white';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Player Name', 140, currentY + 32);
    ctx.fillText('Paid Amount', 350, currentY + 32);
    ctx.fillText('Pending Amount', 525, currentY + 32);

    // Draw vertical lines for columns
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    [280, 420].forEach(x => {
      ctx.beginPath();
      ctx.moveTo(x, currentY);
      ctx.lineTo(x, currentY + tableHeight);
      ctx.stroke();
    });

    currentY += rowHeight;

    // Player rows
    players.filter(p => p.isActive).forEach((player, index) => {
      const isEven = index % 2 === 0;
      const paymentInfo = getPlayerPaymentInfo(player.id);

      // Alternating row background
      ctx.fillStyle = isEven ? '#f9fafb' : 'white';
      ctx.fillRect(0, currentY, width, rowHeight);

      // Row border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, currentY + rowHeight);
      ctx.lineTo(width, currentY + rowHeight);
      ctx.stroke();

      // Player name
      ctx.fillStyle = '#111827';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(player.name, 140, currentY + 32);

      // Paid amount
      ctx.fillStyle = paymentInfo.paid > 0 ? '#059669' : '#6b7280';
      ctx.font = '12px Arial';
      const paidText = paymentInfo.paid > 0 ? `₹${paymentInfo.paid.toLocaleString()}` : '--';
      ctx.fillText(paidText, 350, currentY + 32);

      // Pending amount
      ctx.fillStyle = paymentInfo.pending > 0 ? '#dc2626' : '#6b7280';
      const pendingText = paymentInfo.pending > 0 ? `₹${paymentInfo.pending.toLocaleString()}` : '--';
      ctx.fillText(pendingText, 525, currentY + 32);

      currentY += rowHeight;
    });

    // Footer
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Generated on ${new Date().toLocaleDateString()}`, width/2, height - 20);

    // Download the image
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tournament_fees_summary.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert('Tournament fees summary downloaded! You can now share it on WhatsApp.');
    }, 'image/png');
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
      {/* Accordion Header */}
      <div
        className="accordion-header px-6 py-4 cursor-pointer flex justify-between items-center transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg font-semibold">Tournament Fees</span>
          {!isOpen && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-sm text-gray-300">
                Pending payment - {pendingCount}/{activePlayerCount}
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-sm text-gray-300">
                Rs. {tournamentPerHead.toLocaleString()} /head
              </span>
              <span className="text-gray-300">|</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadTournamentSummary();
                }}
                className="text-sm text-gray-300 hover:text-blue-200 transition-colors cursor-pointer"
              >
                Download Summary
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Accordion Content */}
      {isOpen && (
        <div>
          <table className="w-full border-collapse">
            <thead>
              <tr className="table-header">
                <th className="px-4 py-3 text-left font-medium">Player Name</th>
                <th className="px-4 py-3 text-center font-medium">Paid</th>
                <th className="px-4 py-3 text-center font-medium">Pending</th>
                <th className="px-4 py-3 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {players.filter(p => p.isActive).map((player) => {
                const paymentInfo = getPlayerPaymentInfo(player.id);
                return (
                  <tr key={player.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{player.name}</td>
                    <td className="px-4 py-2 text-center text-sm">
                      {editingPaidColumn === player.id ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="number"
                            value={editPaidAmount}
                            onChange={(e) => setEditPaidAmount(e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                            autoFocus
                            onKeyPress={(e) => e.key === 'Enter' && savePaidEdit()}
                          />
                          <button
                            onClick={savePaidEdit}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelPaidEdit}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span
                          className="cursor-pointer text-blue-600 hover:text-blue-800"
                          onDoubleClick={() => startEditPaid(player.id)}
                        >
                          {paymentInfo.paid > 0 ? `₹${paymentInfo.paid.toLocaleString()}` : '--'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      {editingPendingColumn === player.id ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="number"
                            value={editPendingAmount}
                            onChange={(e) => setEditPendingAmount(e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                            autoFocus
                            onKeyPress={(e) => e.key === 'Enter' && savePendingEdit()}
                          />
                          <button
                            onClick={savePendingEdit}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelPendingEdit}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`cursor-pointer ${paymentInfo.pending > 0 ? 'text-red-600' : 'text-gray-500'}`}
                          onDoubleClick={() => paymentInfo.pending > 0 && startEditPending(player.id)}
                        >
                          {paymentInfo.pending > 0 ? `₹${paymentInfo.pending.toLocaleString()}` : '--'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        paymentInfo.status === 'Full Paid' ? 'bg-green-100 text-green-800' :
                        paymentInfo.status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' :
                        'text-gray-500'
                      }`}>
                        {paymentInfo.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};